import React from 'react';
import classNames from 'classnames';
import ComponentError from 'components/Error/ComponentError';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { handleError } from 'services/ErrorService';
import { useTranslation } from 'react-i18next';
import './styles.scss';
import { Icon, Tooltip } from '@patternfly/react-core';
import {
  ExclamationTriangleIcon,
  InfoCircleIcon,
} from '@patternfly/react-icons';
import { DateTime } from 'luxon';
import { extractFromSlug } from 'helpers/utils';
import useMobxStores from 'hooks/useMobxStores';
import CatalogStore from 'stores/Catalog';

function catalogTitleForOfferingSlug(
  catalogStore: CatalogStore,
  offeringSlug: string,
  fallbackTitle: string,
  language: string,
): string {
  const { code, version } = extractFromSlug(offeringSlug);
  if (!code || !version) {
    return fallbackTitle;
  }

  const entry = catalogStore.getCatalogEntryForCourse(code, version);

  if (!entry) {
    return fallbackTitle;
  }

  return CatalogStore.getTitleWithFallback(entry, language) || fallbackTitle;
}

export type MyEnrollmentsWidgetProps = {
  /** When true, plays a short entrance animation on mount. */
  animateEntrance?: boolean;
};

const MyEnrollmentsWidget = ({
  animateEntrance = true,
}: MyEnrollmentsWidgetProps) => {
  const { t, i18n } = useTranslation();
  const { userStore, catalogStore } = useMobxStores();
  const { enforceLabLimit, entitlementEnablements } = userStore;
  const { sortedEntitlements } = userStore;

  const exclamationIcon = (
    <React.Fragment>
      <Icon status="warning" size="lg" className="exclamation-triangle-icon">
        <ExclamationTriangleIcon />
      </Icon>
    </React.Fragment>
  );

  const infoCircleIcon = (
    <React.Fragment>
      <Icon status="info" size="lg" className="exclamation-triangle-icon">
        <InfoCircleIcon />
      </Icon>
    </React.Fragment>
  );

  const getLabUsageRemaining = (
    used: number = 0,
    allowed: number = 0,
  ): React.ReactElement => {
    const hours = Math.max(allowed - used, 0);

    const labHoursRemainingTxt = <>{t('Lab hours remaining')}:&nbsp;</>;

    if (hours > 20 || !enforceLabLimit) {
      return (
        <>
          {t('Lab hours used')} &nbsp;
          <strong>
            {Math.round(used)}/{allowed}
          </strong>
        </>
      );
    }

    if (hours > 10) {
      return (
        <>
          {infoCircleIcon} {labHoursRemainingTxt}
          <span className="usage-info-alert">
            <strong>
              {t('less than')} {hours}
            </strong>
          </span>
        </>
      );
    }

    if (hours > 0) {
      return (
        <>
          {exclamationIcon} {labHoursRemainingTxt}
          <span className="usage-alert">
            <strong>
              {t('less than')} {hours}
            </strong>
          </span>
        </>
      );
    }

    return (
      <div className="usage-alert-bg">
        <span className="usage-alert">
          <strong>{t('All lab hours have been used')}</strong>
        </span>
      </div>
    );
  };

  const getRemainingEntitlementTime = (endTime: string): number =>
    DateTime.fromISO(endTime).diff(DateTime.now(), 'days').days;

  return (
    <div
      className={classNames('my-enrollments-widget', {
        'my-enrollments-widget--entrance': animateEntrance,
      })}
    >
      {sortedEntitlements.length === 0 ? (
        <strong>{t('No enrollment found')}</strong>
      ) : (
        <>
          {sortedEntitlements.length > 1 && (
            <div
              className="my-enrollments-widget-enrollment-section"
              key="usage-summary"
            >
              <div className="my-enrollments-widget-usage-summary">
                <strong className="my-enrollments-widget-usage-summary-tag">
                  {t('Usage summary')}
                </strong>
                <Tooltip
                  content={
                    <div>
                      {t(
                        'Displays your combined usage across all active purchases. Metrics are aggregated where applicable, such as lab hours and exams, and displayed as a single total',
                      )}
                    </div>
                  }
                  entryDelay={0}
                  exitDelay={0}
                >
                  <Icon status="info">
                    <InfoCircleIcon />
                  </Icon>
                </Tooltip>
              </div>
              {entitlementEnablements.quotas?.lab_hours?.allowed > 0 && (
                <div>
                  {getLabUsageRemaining(
                    entitlementEnablements.quotas.lab_hours.used,
                    entitlementEnablements.quotas.lab_hours.allowed,
                  )}
                </div>
              )}
              {entitlementEnablements.quotas?.exams?.allowed > 0 && (
                <div>
                  <span>{t('Exams used')}</span>
                  <strong>
                    {entitlementEnablements.quotas.exams.used}/
                    {entitlementEnablements.quotas.exams.allowed}
                  </strong>
                </div>
              )}
              {entitlementEnablements.quotas?.experiences?.allowed > 0 && (
                <div>
                  <span>{t('Exam experiences used')}</span>
                  <strong>
                    {entitlementEnablements.quotas.experiences.used}/
                    {entitlementEnablements.quotas.experiences.allowed}
                  </strong>
                </div>
              )}
            </div>
          )}

          {sortedEntitlements.map((entitlement) => {
            const remainingEntitlementTime = getRemainingEntitlementTime(
              entitlement.entitlement_info.end_time,
            );

            return (
              <div
                className="my-enrollments-widget-enrollment-section"
                key={entitlement.entitlement_info.offering_slug}
              >
                {entitlement.entitlement_info.offering_slug ? (
                  <strong className="my-enrollments-widget-section-title-tag">
                    <Tooltip
                      content={catalogTitleForOfferingSlug(
                        catalogStore,
                        entitlement.entitlement_info.offering_slug,
                        entitlement.entitlement_info.title,
                        i18n.language,
                      )}
                      entryDelay={0}
                      exitDelay={0}
                    >
                      <span>{entitlement.entitlement_info.title}</span>
                    </Tooltip>
                  </strong>
                ) : (
                  <strong className="my-enrollments-widget-section-title-tag">
                    {entitlement.entitlement_info.title}
                  </strong>
                )}
                <div>
                  {entitlement.entitlement_info.modality === 'alacarte' &&
                  remainingEntitlementTime > 0 &&
                  remainingEntitlementTime <= 1 ? (
                    <strong>{t('Last day')}</strong>
                  ) : (
                    <>
                      <span>{t('Days remaining')}</span>
                      <strong>{Math.round(remainingEntitlementTime)}</strong>
                    </>
                  )}
                </div>

                {entitlement.quotas?.lab_hours?.allowed > 0 && (
                  <div>
                    {getLabUsageRemaining(
                      entitlement.quotas.lab_hours.used,
                      entitlement.quotas.lab_hours.allowed,
                    )}
                  </div>
                )}
                {entitlement.quotas?.exams?.allowed > 0 && (
                  <div>
                    <span>{t('Exams used')}</span>
                    <strong>
                      {entitlement.quotas.exams.used}/
                      {entitlement.quotas.exams.allowed}
                    </strong>
                  </div>
                )}
                {entitlement.quotas?.experiences?.allowed > 0 && (
                  <div>
                    <span>{t('Exam experiences used')}</span>
                    <strong>
                      {entitlement.quotas.experiences.used}/
                      {entitlement.quotas.experiences.allowed}
                    </strong>
                  </div>
                )}
                {entitlement.quotas?.live_class_enrolled?.allowed > 0 && (
                  <div>
                    <span>{t('Live classes enrolled')}</span>
                    <strong>
                      {entitlement.quotas.live_class_enrolled.used}/
                      {entitlement.quotas.live_class_enrolled.allowed}
                    </strong>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default withErrorBoundary(
  observer(MyEnrollmentsWidget),
  ComponentError,
  handleError,
);
